import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Coins,
  TrendingUp,
  TrendingDown,
  Users,
  CreditCard,
  Crown,
  Sparkles,
  RefreshCw,
  Search,
  CheckCircle2,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownLeft,
  Filter,
  ShieldCheck,
  Award
} from 'lucide-react';
import {
  InspectTokenomicsData,
  fetchInspectTokenomics,
  adminCreditTokens,
  adminSetUserTier
} from '../../services/inspectService';

interface TokenomicsTabProps {
  onRefreshAll?: () => void;
}

export const TokenomicsTab: React.FC<TokenomicsTabProps> = ({ onRefreshAll }) => {
  const [data, setData] = useState<InspectTokenomicsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Credit form state
  const [targetUsername, setTargetUsername] = useState('');
  const [creditAmount, setCreditAmount] = useState<number>(5);
  const [creditReason, setCreditReason] = useState('');
  const [creditType, setCreditType] = useState('admin_grant');
  const [isSubmittingCredit, setIsSubmittingCredit] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState<{ isError: boolean; text: string } | null>(null);

  // Quick tier switch state
  const [selectedTier, setSelectedTier] = useState<'free' | 'creator_pro' | 'studio'>('creator_pro');
  const [isSubmittingTier, setIsSubmittingTier] = useState(false);

  // Filters & Search
  const [userSearch, setUserSearch] = useState('');
  const [ledgerSearch, setLedgerSearch] = useState('');
  const [ledgerActionFilter, setLedgerActionFilter] = useState<string>('all');

  const loadData = useCallback(async (showRefreshingSpinner = false) => {
    if (showRefreshingSpinner) setIsRefreshing(true);
    else setIsLoading(true);

    try {
      const res = await fetchInspectTokenomics();
      if (res) {
        setData(res);
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle Token Credit Submission
  const handleCreditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetUsername.trim() || !creditAmount || isSubmittingCredit) return;

    setIsSubmittingCredit(true);
    setFeedbackMsg(null);

    try {
      const res = await adminCreditTokens({
        username: targetUsername.trim(),
        amount: creditAmount,
        description: creditReason.trim() || undefined,
        actionType: creditType
      });

      if (res.success) {
        setFeedbackMsg({
          isError: false,
          text: res.message || `Successfully adjusted ${creditAmount} tokens for ${targetUsername}!`
        });
        setCreditReason('');
        await loadData(true);
        onRefreshAll?.();
      } else {
        setFeedbackMsg({
          isError: true,
          text: res.error || 'Failed to credit tokens. Check username and try again.'
        });
      }
    } catch (err: any) {
      setFeedbackMsg({ isError: true, text: err.message || 'Network error executing token credit.' });
    } finally {
      setIsSubmittingCredit(false);
    }
  };

  // Handle Subscription Tier Override
  const handleTierSubmit = async () => {
    if (!targetUsername.trim() || isSubmittingTier) return;
    setIsSubmittingTier(true);
    setFeedbackMsg(null);

    try {
      const res = await adminSetUserTier({
        username: targetUsername.trim(),
        tier: selectedTier
      });

      if (res.success) {
        setFeedbackMsg({
          isError: false,
          text: `Updated ${targetUsername} to "${selectedTier.replace('_', ' ').toUpperCase()}" tier!`
        });
        await loadData(true);
        onRefreshAll?.();
      } else {
        setFeedbackMsg({
          isError: true,
          text: res.error || 'Failed to update subscription tier.'
        });
      }
    } catch (err: any) {
      setFeedbackMsg({ isError: true, text: err.message || 'Error updating tier.' });
    } finally {
      setIsSubmittingTier(false);
    }
  };

  // Pre-fill user from table click
  const selectUserForCredit = (username: string, currentTier?: string) => {
    setTargetUsername(username);
    if (currentTier === 'creator_pro' || currentTier === 'studio' || currentTier === 'free') {
      setSelectedTier(currentTier);
    }
    setFeedbackMsg(null);
    window.scrollTo({ top: 320, behavior: 'smooth' });
  };

  // Filtered Holders
  const filteredHolders = useMemo(() => {
    if (!data?.topHolders) return [];
    if (!userSearch.trim()) return data.topHolders;
    const q = userSearch.toLowerCase();
    return data.topHolders.filter(u => u.username.toLowerCase().includes(q));
  }, [data?.topHolders, userSearch]);

  // Filtered Ledger
  const filteredLedger = useMemo(() => {
    if (!data?.recentTransactions) return [];
    let list = data.recentTransactions;

    if (ledgerActionFilter !== 'all') {
      if (ledgerActionFilter === 'spends') {
        list = list.filter(tx => tx.amount < 0);
      } else if (ledgerActionFilter === 'grants') {
        list = list.filter(tx => tx.amount > 0);
      } else {
        list = list.filter(tx => tx.action === ledgerActionFilter);
      }
    }

    if (ledgerSearch.trim()) {
      const q = ledgerSearch.toLowerCase();
      list = list.filter(
        tx =>
          tx.username.toLowerCase().includes(q) ||
          tx.description.toLowerCase().includes(q) ||
          tx.action.toLowerCase().includes(q) ||
          (tx.referenceId && tx.referenceId.toLowerCase().includes(q))
      );
    }

    return list;
  }, [data?.recentTransactions, ledgerActionFilter, ledgerSearch]);

  const stats = data?.stats;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* ────────────────────────────────────────────── */}
      {/* 1. TOP METRICS HEADER                          */}
      {/* ────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1rem'
      }}>
        {/* Circulation Card */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.12) 0%, rgba(15, 23, 42, 0.7) 100%)',
          border: '1px solid rgba(234, 179, 8, 0.35)',
          borderRadius: '14px',
          padding: '1.25rem',
          backdropFilter: 'blur(12px)',
          position: 'relative'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Total Circulation
            </span>
            <Coins size={20} color="#fbbf24" />
          </div>
          <div style={{ fontSize: '2.1rem', fontWeight: 800, color: '#fff', fontFamily: 'var(--font-mono)' }}>
            {isLoading ? '...' : (stats?.totalCirculation ?? 0).toLocaleString()}
            <span style={{ fontSize: '1rem', color: '#fbbf24', marginLeft: '6px', fontWeight: 600 }}>TKN</span>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '10px', fontSize: '0.78rem' }}>
            <span style={{ color: '#34d399', display: 'flex', alignItems: 'center', gap: '3px' }}>
              <ArrowUpRight size={13} /> {stats?.totalEarned ?? 0} minted
            </span>
            <span style={{ color: '#f87171', display: 'flex', alignItems: 'center', gap: '3px' }}>
              <ArrowDownLeft size={13} /> {stats?.totalSpent ?? 0} burned
            </span>
          </div>
        </div>

        {/* Subscriptions Card */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.12) 0%, rgba(15, 23, 42, 0.7) 100%)',
          border: '1px solid rgba(168, 85, 247, 0.35)',
          borderRadius: '14px',
          padding: '1.25rem',
          backdropFilter: 'blur(12px)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#c084fc', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Active Subscriptions
            </span>
            <Crown size={20} color="#c084fc" />
          </div>
          <div style={{ fontSize: '2.1rem', fontWeight: 800, color: '#fff', fontFamily: 'var(--font-mono)' }}>
            {isLoading ? '...' : ((stats?.tiers?.creator_pro ?? 0) + (stats?.tiers?.studio ?? 0))}
            <span style={{ fontSize: '1rem', color: '#c084fc', marginLeft: '6px', fontWeight: 600 }}>PAID</span>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px', fontSize: '0.75rem' }}>
            <span style={{
              background: 'rgba(234, 179, 8, 0.2)',
              color: '#fbbf24',
              padding: '2px 8px',
              borderRadius: '6px',
              border: '1px solid rgba(234, 179, 8, 0.4)'
            }}>
              Pro: {stats?.tiers?.creator_pro ?? 0}
            </span>
            <span style={{
              background: 'rgba(168, 85, 247, 0.2)',
              color: '#c084fc',
              padding: '2px 8px',
              borderRadius: '6px',
              border: '1px solid rgba(168, 85, 247, 0.4)'
            }}>
              Studio: {stats?.tiers?.studio ?? 0}
            </span>
            <span style={{
              background: 'rgba(148, 163, 184, 0.12)',
              color: '#94a3b8',
              padding: '2px 8px',
              borderRadius: '6px'
            }}>
              Free: {stats?.tiers?.free ?? 0}
            </span>
          </div>
        </div>

        {/* Funded Accounts Card */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(15, 23, 42, 0.7) 100%)',
          border: '1px solid rgba(59, 130, 246, 0.35)',
          borderRadius: '14px',
          padding: '1.25rem',
          backdropFilter: 'blur(12px)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Funded Performers
            </span>
            <Users size={20} color="#60a5fa" />
          </div>
          <div style={{ fontSize: '2.1rem', fontWeight: 800, color: '#fff', fontFamily: 'var(--font-mono)' }}>
            {isLoading ? '...' : (stats?.fundedUsersCount ?? 0)}
            <span style={{ fontSize: '1.1rem', color: '#64748b', fontWeight: 500, marginLeft: '6px' }}>
              / {stats?.totalUsersCount ?? 0}
            </span>
          </div>
          <div style={{ marginTop: '10px', fontSize: '0.78rem', color: '#94a3b8' }}>
            {stats?.totalUsersCount
              ? `${Math.round(((stats.fundedUsersCount || 0) / stats.totalUsersCount) * 100)}% of user base holds positive balance`
              : 'No users registered yet'}
          </div>
        </div>

        {/* Activity & Controls Card */}
        <div style={{
          background: 'rgba(15, 23, 42, 0.7)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '14px',
          padding: '1.25rem',
          backdropFilter: 'blur(12px)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#e2e8f0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Ledger Health
            </span>
            <button
              type="button"
              onClick={() => loadData(true)}
              disabled={isRefreshing}
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                color: '#e2e8f0',
                borderRadius: '8px',
                padding: '5px 10px',
                cursor: 'pointer',
                fontSize: '0.78rem',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              <RefreshCw size={13} className={isRefreshing ? 'spin' : ''} />
              {isRefreshing ? 'Syncing...' : 'Refresh'}
            </button>
          </div>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#fff', marginTop: '6px' }}>
            {data?.recentTransactions?.length ?? 0} Recorded Tx
          </div>
          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
            Double-entry ledger with optimistic locking & audit logs
          </div>
        </div>
      </div>

      {/* ────────────────────────────────────────────── */}
      {/* 2. ADMIN CREDIT & MEMBERSHIP CONSOLE           */}
      {/* ────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.75) 0%, rgba(15, 23, 42, 0.9) 100%)',
        border: '1px solid rgba(234, 179, 8, 0.3)',
        borderRadius: '16px',
        padding: '1.5rem',
        backdropFilter: 'blur(16px)',
        boxShadow: '0 10px 30px rgba(0,0,0,0.35)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.25rem' }}>
          <div style={{
            background: 'rgba(234, 179, 8, 0.2)',
            border: '1px solid rgba(234, 179, 8, 0.4)',
            borderRadius: '10px',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Sparkles size={20} color="#fbbf24" />
          </div>
          <div>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#fff', margin: 0 }}>
              Admin Token Credit & Membership Console
            </h3>
            <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '3px 0 0 0' }}>
              Directly credit tokens, compensate performers, or grant membership tiers with immediate D1 ledger logging.
            </p>
          </div>
        </div>

        {/* Feedback Alert */}
        {feedbackMsg && (
          <div style={{
            background: feedbackMsg.isError ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)',
            border: `1px solid ${feedbackMsg.isError ? 'rgba(239, 68, 68, 0.4)' : 'rgba(16, 185, 129, 0.4)'}`,
            borderRadius: '10px',
            padding: '10px 14px',
            marginBottom: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            color: feedbackMsg.isError ? '#fca5a5' : '#6ee7b7',
            fontSize: '0.88rem'
          }}>
            {feedbackMsg.isError ? <AlertTriangle size={18} /> : <CheckCircle2 size={18} />}
            <span>{feedbackMsg.text}</span>
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '1.5rem'
        }}>
          {/* Form 1: Credit / Debit Tokens */}
          <form onSubmit={handleCreditSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Coins size={16} color="#fbbf24" /> Credit / Debit Tokens
            </div>

            {/* Target Username */}
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>
                Target Username *
              </label>
              <input
                type="text"
                required
                placeholder="e.g. Dan, Alice, Creator123"
                value={targetUsername}
                onChange={(e) => setTargetUsername(e.target.value)}
                style={{
                  width: '100%',
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '8px',
                  padding: '9px 12px',
                  color: '#fff',
                  fontSize: '0.9rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Quick Amount Chips */}
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>
                Quick Presets
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {[1, 5, 10, 25, 50, 100, -5].map((amt) => (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setCreditAmount(amt)}
                    style={{
                      padding: '5px 11px',
                      borderRadius: '8px',
                      border: creditAmount === amt ? '1px solid #fbbf24' : '1px solid rgba(255, 255, 255, 0.1)',
                      background: creditAmount === amt ? 'rgba(234, 179, 8, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                      color: amt < 0 ? '#f87171' : creditAmount === amt ? '#fbbf24' : '#e2e8f0',
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'var(--font-mono)'
                    }}
                  >
                    {amt > 0 ? `+${amt}` : amt}
                  </button>
                ))}
              </div>
            </div>

            {/* Amount input & action type */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: '10px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>
                  Amount *
                </label>
                <input
                  type="number"
                  required
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(parseInt(e.target.value, 10) || 0)}
                  style={{
                    width: '100%',
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '8px',
                    padding: '9px 12px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    fontFamily: 'var(--font-mono)',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>
                  Ledger Action Type
                </label>
                <select
                  value={creditType}
                  onChange={(e) => setCreditType(e.target.value)}
                  style={{
                    width: '100%',
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '8px',
                    padding: '9px 12px',
                    color: '#fff',
                    fontSize: '0.85rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="admin_grant">admin_grant (Courtesy/Grant)</option>
                  <option value="signup_bonus">signup_bonus (Welcome)</option>
                  <option value="daily_streak">daily_streak (Streak)</option>
                  <option value="colab_bounty">colab_bounty (Duet Bounty)</option>
                  <option value="pack_purchase">pack_purchase (Stripe Simulation)</option>
                  <option value="admin_adjustment">admin_adjustment (Correction)</option>
                </select>
              </div>
            </div>

            {/* Description / Reason */}
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>
                Audit Reason / Reference Note
              </label>
              <input
                type="text"
                placeholder="e.g. VIP Creator reward, Customer support grant, Beta compensation"
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
                style={{
                  width: '100%',
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '8px',
                  padding: '9px 12px',
                  color: '#fff',
                  fontSize: '0.85rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmittingCredit || !targetUsername.trim() || creditAmount === 0}
              style={{
                marginTop: '4px',
                background: creditAmount < 0
                  ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                  : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '11px',
                fontWeight: 700,
                fontSize: '0.92rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: (!targetUsername.trim() || creditAmount === 0 || isSubmittingCredit) ? 0.6 : 1
              }}
            >
              <Coins size={16} />
              {isSubmittingCredit
                ? 'Applying Ledger Update...'
                : creditAmount < 0
                  ? `Deduct ${Math.abs(creditAmount)} Tokens`
                  : `Credit ${creditAmount} Tokens to ${targetUsername || 'User'}`}
            </button>
          </form>

          {/* Form 2: Membership Tier Override */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
            paddingLeft: '1.5rem'
          }}>
            <div>
              <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '1rem' }}>
                <Crown size={16} color="#c084fc" /> Manage Subscription Tier
              </div>

              <p style={{ fontSize: '0.82rem', color: '#94a3b8', lineHeight: '1.4', marginBottom: '1rem' }}>
                Manually grant or revoke unlimited perk access (Clean exports, Lossless WAV downloads, Cinema Match DSP) for <strong>{targetUsername || 'selected user'}</strong>.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '1.25rem' }}>
                {(['free', 'creator_pro', 'studio'] as const).map((tier) => {
                  const isSelected = selectedTier === tier;
                  const label = tier === 'creator_pro' ? 'Creator Pro ($9.99/mo)' : tier === 'studio' ? 'Studio Agency ($29.99/mo)' : 'Free Performer Tier';
                  return (
                    <button
                      key={tier}
                      type="button"
                      onClick={() => setSelectedTier(tier)}
                      style={{
                        padding: '10px 14px',
                        borderRadius: '10px',
                        border: isSelected
                          ? tier === 'free'
                            ? '1px solid #94a3b8'
                            : tier === 'creator_pro'
                              ? '1px solid #fbbf24'
                              : '1px solid #c084fc'
                          : '1px solid rgba(255, 255, 255, 0.08)',
                        background: isSelected
                          ? tier === 'free'
                            ? 'rgba(148, 163, 184, 0.15)'
                            : tier === 'creator_pro'
                              ? 'rgba(234, 179, 8, 0.2)'
                              : 'rgba(168, 85, 247, 0.2)'
                          : 'rgba(255, 255, 255, 0.04)',
                        color: isSelected ? '#fff' : '#94a3b8',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontWeight: isSelected ? 700 : 500,
                        fontSize: '0.85rem'
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {tier === 'creator_pro' && <Crown size={15} color="#fbbf24" />}
                        {tier === 'studio' && <Sparkles size={15} color="#c084fc" />}
                        {label}
                      </span>
                      {isSelected && <CheckCircle2 size={16} color="#34d399" />}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              onClick={handleTierSubmit}
              disabled={isSubmittingTier || !targetUsername.trim()}
              style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '11px',
                fontWeight: 700,
                fontSize: '0.92rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: (!targetUsername.trim() || isSubmittingTier) ? 0.6 : 1
              }}
            >
              <Crown size={16} />
              {isSubmittingTier ? 'Updating Tier...' : `Set Tier for ${targetUsername || 'User'}`}
            </button>
          </div>
        </div>
      </div>

      {/* ────────────────────────────────────────────── */}
      {/* 3. USERS & TOKEN BALANCES TABLE                */}
      {/* ────────────────────────────────────────────── */}
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-glass)',
        borderRadius: '16px',
        padding: '1.25rem',
        backdropFilter: 'blur(12px)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
          marginBottom: '1.25rem'
        }}>
          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Users size={18} color="#60a5fa" /> Performer Token Vaults & Balances
            </h3>
            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
              Showing {filteredHolders.length} performers ranked by balance
            </span>
          </div>

          <div style={{ position: 'relative', width: '260px' }}>
            <Search size={14} color="#64748b" style={{ position: 'absolute', left: '10px', top: '10px' }} />
            <input
              type="text"
              placeholder="Search user..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              style={{
                width: '100%',
                background: 'rgba(15, 23, 42, 0.8)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: '8px',
                padding: '7px 10px 7px 30px',
                color: '#fff',
                fontSize: '0.82rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        {/* Table container */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', color: '#94a3b8', textAlign: 'left' }}>
                <th style={{ padding: '10px 12px' }}>Performer</th>
                <th style={{ padding: '10px 12px' }}>Tier</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Current Balance</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Total Earned</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Total Spent</th>
                <th style={{ padding: '10px 12px', textAlign: 'center' }}>Quick Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredHolders.map((user) => {
                const isSelected = targetUsername.toLowerCase() === user.username.toLowerCase();
                const isPro = user.subscriptionTier === 'creator_pro';
                const isStudio = user.subscriptionTier === 'studio';

                return (
                  <tr
                    key={user.id}
                    style={{
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                      background: isSelected ? 'rgba(234, 179, 8, 0.08)' : 'transparent',
                      transition: 'background 0.15s ease'
                    }}
                  >
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 600, color: '#fff' }}>{user.username}</span>
                      </div>
                    </td>

                    <td style={{ padding: '10px 12px' }}>
                      {isPro ? (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.3) 0%, rgba(202, 138, 4, 0.3) 100%)',
                          border: '1px solid #fbbf24',
                          color: '#fbbf24',
                          padding: '2px 8px',
                          borderRadius: '6px',
                          fontSize: '0.72rem',
                          fontWeight: 700
                        }}>
                          <Crown size={11} /> PRO
                        </span>
                      ) : isStudio ? (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: 'rgba(168, 85, 247, 0.3)',
                          border: '1px solid #c084fc',
                          color: '#c084fc',
                          padding: '2px 8px',
                          borderRadius: '6px',
                          fontSize: '0.72rem',
                          fontWeight: 700
                        }}>
                          <Sparkles size={11} /> STUDIO
                        </span>
                      ) : (
                        <span style={{
                          background: 'rgba(148, 163, 184, 0.15)',
                          color: '#94a3b8',
                          padding: '2px 8px',
                          borderRadius: '6px',
                          fontSize: '0.72rem'
                        }}>
                          FREE
                        </span>
                      )}
                    </td>

                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: (user.tokensBalance ?? 0) > 0 ? '#fbbf24' : '#64748b',
                        fontWeight: 700,
                        fontSize: '0.92rem'
                      }}>
                        <Coins size={14} />
                        {(user.tokensBalance ?? 0).toLocaleString()}
                      </span>
                    </td>

                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: '#34d399' }}>
                      +{(user.totalTokensEarned ?? 0).toLocaleString()}
                    </td>

                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: '#f87171' }}>
                      -{(user.totalTokensSpent ?? 0).toLocaleString()}
                    </td>

                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => selectUserForCredit(user.username, user.subscriptionTier)}
                        style={{
                          background: isSelected ? 'rgba(234, 179, 8, 0.3)' : 'rgba(255, 255, 255, 0.08)',
                          border: isSelected ? '1px solid #fbbf24' : '1px solid rgba(255, 255, 255, 0.15)',
                          color: isSelected ? '#fbbf24' : '#e2e8f0',
                          borderRadius: '6px',
                          padding: '4px 10px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Sparkles size={12} /> {isSelected ? 'Selected' : 'Credit / Edit'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ────────────────────────────────────────────── */}
      {/* 4. RECENT TRANSACTIONS LEDGER                  */}
      {/* ────────────────────────────────────────────── */}
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-glass)',
        borderRadius: '16px',
        padding: '1.25rem',
        backdropFilter: 'blur(12px)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CreditCard size={18} color="#fbbf24" /> Live Double-Entry Ledger (Last 100 Transactions)
            </h3>
            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
              Immutable record of all token spends, grants, purchases, and streaks
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', width: '220px' }}>
              <Search size={14} color="#64748b" style={{ position: 'absolute', left: '10px', top: '10px' }} />
              <input
                type="text"
                placeholder="Search ledger..."
                value={ledgerSearch}
                onChange={(e) => setLedgerSearch(e.target.value)}
                style={{
                  width: '100%',
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '8px',
                  padding: '7px 10px 7px 30px',
                  color: '#fff',
                  fontSize: '0.82rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <select
              value={ledgerActionFilter}
              onChange={(e) => setLedgerActionFilter(e.target.value)}
              style={{
                background: 'rgba(15, 23, 42, 0.8)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: '8px',
                padding: '7px 10px',
                color: '#fff',
                fontSize: '0.82rem',
                outline: 'none'
              }}
            >
              <option value="all">All Actions</option>
              <option value="grants">Credits (+)</option>
              <option value="spends">Spends (-)</option>
              <option value="admin_grant">admin_grant</option>
              <option value="pack_purchase">pack_purchase</option>
              <option value="subscription_grant">subscription_grant</option>
              <option value="daily_streak">daily_streak</option>
              <option value="colab_bounty">colab_bounty</option>
              <option value="demucs_split">demucs_split</option>
              <option value="clean_export">clean_export</option>
              <option value="audio_filter">audio_filter</option>
            </select>
          </div>
        </div>

        {/* Ledger Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', color: '#94a3b8', textAlign: 'left' }}>
                <th style={{ padding: '9px 12px' }}>Timestamp</th>
                <th style={{ padding: '9px 12px' }}>Performer</th>
                <th style={{ padding: '9px 12px' }}>Action Type</th>
                <th style={{ padding: '9px 12px', textAlign: 'right' }}>Amount</th>
                <th style={{ padding: '9px 12px', textAlign: 'right' }}>Balance After</th>
                <th style={{ padding: '9px 12px' }}>Description / Note</th>
                <th style={{ padding: '9px 12px' }}>Reference</th>
              </tr>
            </thead>
            <tbody>
              {filteredLedger.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                    No transactions matching the active filter.
                  </td>
                </tr>
              ) : (
                filteredLedger.map((tx) => {
                  const isPositive = tx.amount > 0;
                  const dateStr = new Date(tx.createdAt).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  });

                  return (
                    <tr
                      key={tx.id}
                      style={{
                        borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
                        transition: 'background 0.15s ease'
                      }}
                    >
                      <td style={{ padding: '9px 12px', color: '#94a3b8', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)' }}>
                        {dateStr}
                      </td>

                      <td style={{ padding: '9px 12px', fontWeight: 600, color: '#fff' }}>
                        {tx.username || 'System / Guest'}
                      </td>

                      <td style={{ padding: '9px 12px' }}>
                        <span style={{
                          background: isPositive ? 'rgba(52, 211, 153, 0.15)' : 'rgba(248, 113, 113, 0.15)',
                          color: isPositive ? '#34d399' : '#f87171',
                          border: `1px solid ${isPositive ? 'rgba(52, 211, 153, 0.3)' : 'rgba(248, 113, 113, 0.3)'}`,
                          padding: '2px 7px',
                          borderRadius: '6px',
                          fontSize: '0.72rem',
                          fontFamily: 'var(--font-mono)'
                        }}>
                          {tx.action}
                        </span>
                      </td>

                      <td style={{
                        padding: '9px 12px',
                        textAlign: 'right',
                        fontWeight: 700,
                        fontFamily: 'var(--font-mono)',
                        color: isPositive ? '#34d399' : '#f87171'
                      }}>
                        {isPositive ? `+${tx.amount}` : tx.amount}
                      </td>

                      <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: '#fbbf24' }}>
                        {tx.balanceAfter}
                      </td>

                      <td style={{ padding: '9px 12px', color: '#e2e8f0', maxWidth: '320px', wordBreak: 'break-word' }}>
                        {tx.description}
                      </td>

                      <td style={{ padding: '9px 12px', color: '#64748b', fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>
                        {tx.referenceId || '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
